import type {
  CardDisplay,
  CardFormat,
  ChartCard,
  TrendDirection,
} from '@/features/dashboard/api/schemas'

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
}

const MAGNITUDES = [
  { size: 1e9, suffix: 'B' },
  { size: 1e6, suffix: 'M' },
  { size: 1e3, suffix: 'K' },
] as const

export const currencySymbol = (unit: string | null | undefined) =>
  unit ? CURRENCY_SYMBOLS[unit] : undefined

/** Fractional change between two values, or null when it can't be derived. */
export const changeFraction = (
  value: number,
  previousValue: number | null | undefined
): number | null =>
  previousValue == null || previousValue === 0
    ? null
    : (value - previousValue) / Math.abs(previousValue)

/** e.g. 0.0593 -> '+5.9%'. */
export const formatChange = (fraction: number): string => {
  const sign = fraction > 0 ? '+' : fraction < 0 ? '−' : ''
  return `${sign}${(Math.abs(fraction) * 100).toFixed(1)}%`
}

/**
 * How a change reads: relative (a fraction), ppt / pts (the plain difference),
 * or abs (the plain difference in the card's own unit, for a negative base).
 */
export type ChangeUnit = CardDisplay['changeUnit'] | 'abs'

/** Whether the change's arrow already tells its direction, so the text needs no sign. */
export const toldByArrow = (unit: ChangeUnit) =>
  unit === 'relative' || unit === 'abs'

/**
 * A change in its unit: relative as '+5.9%' (a fraction),
 * ppt as '+1.0 ppt' and pts as '+2 pts' (the plain difference, in percentage
 * points / points). The space is non-breaking so the unit never wraps alone.
 */
export const formatChangeIn = (
  amount: number,
  unit: ChangeUnit = 'relative',
  value?: ValueOptions
): string => {
  if (unit === 'relative') return formatChange(amount)
  const sign = amount > 0 ? '+' : amount < 0 ? '−' : ''
  const size = Math.abs(amount)
  if (unit === 'abs') {
    // Two decimals: -€0.02M to €31.75M is +€31.77M.
    const text =
      value?.format?.style === 'percent'
        ? formatValue(size, { format: value.format })
        : formatAbbrev(size, value?.unit, 2)
    return `${sign}${text}`
  }
  return unit === 'ppt'
    ? `${sign}${size.toFixed(1)}\u00a0ppt`
    : `${sign}${Number(size.toFixed(1))}\u00a0pts`
}

export const trendFromChange = (fraction: number | null): TrendDirection =>
  fraction === null || fraction === 0 ? 'flat' : fraction > 0 ? 'up' : 'down'

type ValueParts = {
  /** Sign and currency symbol, in that order, e.g. '-€'. */
  prefix: string
  main: string
  /** Magnitude letter (K/M/B), rendered smaller next to the figure. */
  suffix: string
  /** Trailing unit for non-currency units, e.g. '%'. */
  unit: string
}

export type ValueOptions = {
  unit?: string | null | undefined
  format?: CardFormat | undefined
  /**
   * kpi: an abbreviated value keeps one decimal (9.9M). chart: it is a whole number (€557M).
   */
  kind?: 'kpi' | 'chart'
  /** Decimals an abbreviated value keeps (1.06M for 2), instead of the kind's. */
  valueDecimals?: number | null | undefined
}

const ISO_CURRENCY = /^[A-Z]{3}$/

const currencyPrefix = (unit: string | null | undefined) => {
  if (!unit) return ''
  return CURRENCY_SYMBOLS[unit] ?? `${unit} `
}

/**
 * A headline figure in pieces: percent cards
 * hold fractions and always show one decimal (0.1217 -> '12.2%', whatever
 * metabase's own decimals say); other values abbreviate as K/M/B, a value that
 * rounds to 1000 of its magnitude carrying up (999.6K -> 1M). Below 1000 a
 * chart value is a whole number, while a kpi integer is plain and anything
 * else has 2 decimals.
 */
export const formatValueParts = (
  value: number,
  { unit, format, kind = 'chart', valueDecimals }: ValueOptions = {}
): ValueParts => {
  const style = format?.style ?? 'number'
  const abs = Math.abs(value)

  if (style === 'percent') {
    // One decimal, unless the card asks for another (0: a whole percent).
    const main = (abs * 100).toFixed(valueDecimals ?? 1)
    return {
      prefix: value < 0 && Number(main) !== 0 ? '-' : '',
      main,
      suffix: '',
      unit: '%',
    }
  }

  const sign = value < 0 ? '-' : ''
  const prefix = `${sign}${style === 'currency' ? currencyPrefix(unit) : ''}`
  const trailing =
    style === 'number' && unit && !ISO_CURRENCY.test(unit) ? unit : ''
  if (abs < 1000) {
    const main =
      kind === 'chart' || Number.isInteger(abs)
        ? String(Math.round(abs))
        : abs.toFixed(2)
    return { prefix, main, suffix: '', unit: trailing }
  }

  const digits = valueDecimals ?? (kind === 'kpi' ? 1 : 0)
  const scaled = (size: number) => Number((abs / size).toFixed(digits))
  let index = MAGNITUDES.findIndex(({ size }) => abs >= size)
  if (index > 0 && scaled(MAGNITUDES[index]?.size ?? 1) >= 1000) index -= 1
  const magnitude = MAGNITUDES[index]
  return {
    prefix,
    main: (abs / (magnitude?.size ?? 1)).toFixed(digits),
    suffix: magnitude?.suffix ?? '',
    unit: trailing,
  }
}

/** The figure as plain text, e.g. '€557M' or '12%'. */
export const formatValue = (value: number, options?: ValueOptions): string => {
  const { prefix, main, suffix, unit } = formatValueParts(value, options)
  return `${prefix}${main}${suffix}${unit}`
}

/**
 * A chart card's per-category totals, falling back to its first series that is
 * not a benchmark: those are never part of a total.
 */
export const chartTotals = (
  card: Pick<ChartCard, 'totals' | 'series'>
): (number | null)[] =>
  card.totals.length > 0
    ? card.totals
    : (card.series.find(series => series.reference !== true)?.data ?? [])

/** Axis/label abbreviation, e.g. 7200000 -> '€7.2M'. */
export const formatAbbrev = (
  value: number,
  unit?: string | null | undefined,
  decimals = 1
): string => {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  const symbol = currencySymbol(unit) ?? ''
  const magnitude = MAGNITUDES.find(({ size }) => abs >= size)
  if (!magnitude) return `${sign}${symbol}${abs.toFixed(0)}`
  return `${sign}${symbol}${(abs / magnitude.size).toFixed(decimals)}${magnitude.suffix}`
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const YEAR = /^(\d{4})$/
const QUARTER = /^(\d{4})-Q([1-4])$/
const DATE = /^(\d{4})-(\d{2})(?:-(\d{2}))?/

type Period =
  | { kind: 'year'; year: string }
  | { kind: 'quarter'; year: string; quarter: string }
  | { kind: 'month'; year: string; month: string }
  | { kind: 'day'; year: string; month: string; day: number }

/**
 * Reads the period strings Managerium sends from Metabase: '2026-07' and
 * '2026-07-01' (a month; any other day of one is a day), '2026-Q3' (a quarter)
 * and '2026' (a year). Anything else (a market, a stage) is no period.
 */
const parsePeriod = (category: string): Period | null => {
  const year = YEAR.exec(category)
  if (year?.[1]) return { kind: 'year', year: year[1] }
  const quarter = QUARTER.exec(category)
  if (quarter?.[1] && quarter[2]) {
    return { kind: 'quarter', year: quarter[1], quarter: quarter[2] }
  }
  const date = DATE.exec(category)
  const month = MONTH_NAMES[Number(date?.[2]) - 1]
  if (!date?.[1] || !month) return null
  const day = Number(date[3])
  return day > 1
    ? { kind: 'day', year: date[1], month, day }
    : { kind: 'month', year: date[1], month }
}

/** The label of a period: quarters and years read the same wherever they show. */
const formatPeriod = (
  category: string,
  month: (name: string, year: string) => string,
  day: (name: string, year: string, day: number) => string
): string => {
  const period = parsePeriod(category)
  switch (period?.kind) {
    case undefined:
      return category
    case 'year':
      return period.year
    case 'quarter':
      return `Q${period.quarter} ${period.year}`
    case 'month':
      return month(period.month, period.year)
    case 'day':
      return day(period.month, period.year, period.day)
  }
}

/**
 * An axis label: '2026-07' -> 'Jul 26', '2026-07-15' -> '15 Jul', '2026-Q3' ->
 * 'Q3 2026', '2026' -> '2026'. Anything that isn't a period passes through.
 */
export const formatMonthShort = (category: string): string =>
  formatPeriod(
    category,
    (name, year) => `${name.slice(0, 3)} ${year.slice(2)}`,
    (name, _, day) => `${day} ${name.slice(0, 3)}`
  )

/** A tooltip label: '2026-07' -> 'July 2026', '2026-07-15' -> '15 July 2026', '2026-Q3' -> 'Q3 2026'. */
export const formatMonthLong = (category: string): string =>
  formatPeriod(
    category,
    (name, year) => `${name} ${year}`,
    (name, year, day) => `${day} ${name} ${year}`
  )

/** 'September' for '2026-09...'; the current month for anything else. */
export const formatMonthName = (category: string | undefined): string => {
  const date = DATE.exec(category ?? '')
  return (
    MONTH_NAMES[Number(date?.[2]) - 1] ??
    MONTH_NAMES[new Date().getMonth()] ??
    ''
  )
}

/** 'September 2026' for '2026-09...'; anything else as it is. */
export const formatMonthYearLong = (category: string): string => {
  const date = DATE.exec(category)
  const month = MONTH_NAMES[Number(date?.[2]) - 1]
  return date && month ? `${month} ${date[1]}` : category
}

/** '2026-Q3' for a quarter or a month within it; anything else as it is. */
export const formatQuarterLabel = (category: string): string => {
  const date = DATE.exec(category)
  return date ? `${date[1]}-Q${Math.ceil(Number(date[2]) / 3)}` : category
}

/**
 * A deep-dive label: '2026-07' -> 'Jul 2026', '2026-Q3' -> 'Q3 2026', '2026' ->
 * '2026'. With `monthOnly` (the card's `dateFormat: month`) a day of a month
 * reads as the month too: '2026-07-15' -> 'Jul 2026'.
 */
export const formatMonthYear = (category: string, monthOnly = false): string =>
  formatPeriod(
    category,
    (name, year) => `${name.slice(0, 3)} ${year}`,
    (name, year, day) =>
      monthOnly
        ? `${name.slice(0, 3)} ${year}`
        : `${day} ${name.slice(0, 3)} ${year}`
  )

/** Whether every category is a month (or finer), so a month format applies. */
export const isMonthly = (categories: string[]): boolean =>
  categories.length > 0 &&
  categories.every(category => {
    const kind = parsePeriod(category)?.kind
    return kind === 'month' || kind === 'day'
  })

/**
 * Sentence case, keeping acronyms, for a deep dive title:
 * 'Revenue Deep Dive' -> 'Revenue deep dive', 'PAU Deep Dive' -> 'PAU deep dive'.
 */
export const sentenceCase = (title: string) =>
  title.replace(
    / (?![A-Z]{2,}\b)(\w+)/g,
    (_, word: string) => ` ${word.toLowerCase()}`
  )
